import { useEffect, useMemo, useRef, useState } from "react";

// Micro-animazione astratta "flussi di lavoro" per la Home Ufficio Desktop (solo Preview).
// Traiettorie curve sottili a tutta larghezza (scorrono anche dietro al saluto) + nodi luminosi sobri.
// I nodi sono quasi invisibili sul lato sinistro (dietro al nome) e guadagnano intensità spostandosi a destra.
// Leggera: SVG + requestAnimationFrame, sospesa se pagina nascosta o prefers-reduced-motion.

const VB_W = 1000;
const VB_H = 140;
const PATHS = [
  "M-40,42 C220,64 420,60 640,44 S960,58 1040,46",
  "M-40,58 C220,40 430,86 660,66 S980,44 1040,58",
  "M-40,84 C240,98 470,56 700,80 S980,94 1040,74",
  "M-40,100 C220,82 430,102 640,86 S960,100 1040,96",
];
// Palette ufficiale area Account Ufficio (viola/lilla + antracite).
const COLORS = ["#6D5FB8", "#8478C4", "#A99BD6", "#C4B7E6", "#3A3A4C"];

// Dissolvenza orizzontale: molto tenue dietro al nome (x piccolo) → piena allontanandosi a destra.
const FADE_START = 130; // viewBox x: inizio zona "dietro al nome"
const FADE_END = 440;   // viewBox x: intensità piena
const fadeAt = (x) => {
  const k = Math.max(0, Math.min(1, (x - FADE_START) / (FADE_END - FADE_START)));
  return 0.05 + k * 0.95;
};

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    let mq;
    try {
      mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      setReduce(mq.matches);
      const h = () => setReduce(mq.matches);
      mq.addEventListener("change", h);
      return () => mq.removeEventListener("change", h);
    } catch (_) { /* noop */ }
  }, []);
  return reduce;
}

export function OrbitFlow({ count = 0, urgent = 0 }) {
  const n = Math.max(0, Math.min(10, count | 0));
  const urgentN = Math.max(0, Math.min(n, urgent | 0));
  const reduce = usePrefersReducedMotion();
  const pathRefs = useRef([]);
  const dotRefs = useRef([]);
  const rafRef = useRef(0);

  // Parametri dei nodi: ricostruiti SOLO quando cambia n o il numero di urgenti.
  const particles = useMemo(() => Array.from({ length: n }, (_, i) => ({
    pathIndex: i % PATHS.length,
    speed: 0.016 + (i % 3) * 0.005,   // lento
    t: (i + 0.5) / Math.max(1, n),     // fase iniziale distribuita
    r: 2.6 + (i % 2) * 0.9,
    color: COLORS[i % COLORS.length],
    urgent: i < urgentN,               // i primi nodi rappresentano azioni urgenti
  })), [n, urgentN]);

  // Posiziona un nodo lungo la traiettoria e ne modula l'opacità in base alla x (dissolvenza dietro al nome).
  const place = (p, dotG) => {
    const path = pathRefs.current[p.pathIndex];
    if (!path || !dotG) return;
    let pt;
    try { pt = path.getPointAtLength(p.t * path.getTotalLength()); } catch (_) { return; }
    dotG.setAttribute("transform", `translate(${pt.x.toFixed(2)},${pt.y.toFixed(2)})`);
    dotG.setAttribute("opacity", fadeAt(pt.x).toFixed(3));
  };

  useEffect(() => {
    if (reduce || n === 0) {
      particles.forEach((p, i) => place(p, dotRefs.current[i]));
      return;
    }
    let last = performance.now();
    const step = (now) => {
      const dt = Math.min(48, now - last) / 1000;
      last = now;
      particles.forEach((p, i) => {
        p.t += p.speed * dt;
        if (p.t > 1) p.t -= 1;
        place(p, dotRefs.current[i]);
      });
      rafRef.current = requestAnimationFrame(step);
    };
    const start = () => { if (!rafRef.current) { last = performance.now(); rafRef.current = requestAnimationFrame(step); } };
    const stop = () => { if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; } };
    const onVis = () => { if (document.hidden) stop(); else start(); };
    document.addEventListener("visibilitychange", onVis);
    if (!document.hidden) start();
    return () => { document.removeEventListener("visibilitychange", onVis); stop(); };
  }, [n, reduce, particles]);

  return (
    <div data-testid="orbit-flow" aria-hidden="true"
      className="pointer-events-none w-full h-full overflow-visible">
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="xMidYMid slice" className="w-full h-full" style={{ overflow: "visible" }}>
        {/* Traiettorie: linee sottili in palette Orbit, molto tenui (attenuate a sinistra, dietro al nome) */}
        <defs>
          <linearGradient id="orbitLineFade" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#B9BEDD" stopOpacity="0.05" />
            <stop offset="0.32" stopColor="#B9BEDD" stopOpacity="0.28" />
            <stop offset="1" stopColor="#B9BEDD" stopOpacity="0.34" />
          </linearGradient>
        </defs>
        <g fill="none" stroke="url(#orbitLineFade)" strokeWidth="1" strokeLinecap="round"
          opacity={n === 0 ? 0.55 : 1}>
          {PATHS.map((d, i) => (
            <path key={i} ref={(el) => (pathRefs.current[i] = el)} d={d} />
          ))}
        </g>
        {/* Nodi: alone sobrio + punto luminoso; urgenti in rosso e pulsanti */}
        {particles.map((p, i) => (
          <g key={i} ref={(el) => (dotRefs.current[i] = el)}>
            {p.urgent ? (
              <g className={reduce ? "" : "orbit-node-pulse"}>
                <circle r={(p.r * 0.5 + 2.2).toFixed(2)} fill="#E5484D" opacity="0.24" />
                <circle r={(p.r * 0.5 + 0.2).toFixed(2)} fill="#E5484D" opacity="0.96" />
              </g>
            ) : (
              <>
                <circle r={(p.r * 0.5 + 2.0).toFixed(2)} fill={p.color} opacity="0.16" />
                <circle r={(p.r * 0.5 + 0.1).toFixed(2)} fill={p.color} opacity="0.92" />
              </>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}

export default OrbitFlow;
